import { useLayoutEffect, type RefObject } from 'react';

/**
 * The toolbar's controls that the bulk bar lies over cannot take focus while
 * it does.
 *
 * The bar keeps its place at the end of the toolbar's row and is drawn over
 * whatever of the toolbar reaches it (Jason, 2 Oct 2026). A control under it
 * would still be reached by Tab, and take the focus somewhere nobody can see.
 * A control is under the bar when their boxes meet, so one sitting in the gap
 * before the bar stays usable, and so does everything when the bar has wrapped
 * onto a row of its own on a very narrow window. One partly under the bar is
 * inert whole: its visible part is not worth a focus ring nobody can see.
 *
 * Which controls those are changes without the toolbar's box changing: a
 * Filter count appearing widens one control and pushes the rest along (review,
 * 2 Oct 2026). So every control is watched, along with the bar and the row,
 * and the row's children are read afresh each time. None is left inert once
 * the bar goes. The row and the bar are taken when the bar appears; neither is
 * remounted while a selection lasts.
 *
 * A control that has the focus when it goes under the bar hands it to the
 * bar's first button that can take it, or to the bar itself while a bulk
 * action has them all disabled. Made inert with the focus in it, it would
 * drop the focus to the page and the place in the Tab order with it (review
 * rounds 2 and 3).
 */
export function useInertUnderBar(
  row: RefObject<HTMLElement | null>,
  bar: RefObject<HTMLElement | null>,
  covering: boolean,
): void {
  useLayoutEffect(() => {
    const controls = row.current;
    const cover = bar.current;
    if (!controls || !cover || !covering) return undefined;
    const marked = new Set<HTMLElement>();
    const watched = new Set<Element>();
    const meets = (a: DOMRect, b: DOMRect) => a.right > b.left + 0.5 && a.left < b.right - 0.5
      && a.bottom > b.top + 0.5 && a.top < b.bottom - 0.5;
    const mark = () => {
      const bounds = cover.getBoundingClientRect();
      const current = Array.from(controls.children) as HTMLElement[];
      for (const control of marked) {
        if (!current.includes(control)) {
          control.inert = false;
          marked.delete(control);
        }
      }
      for (const control of current) {
        if (!watched.has(control)) {
          watched.add(control);
          observer.observe(control);
        }
        const under = meets(control.getBoundingClientRect(), bounds);
        if (under && !control.inert && control.contains(document.activeElement)) {
          (cover.querySelector<HTMLElement>('button:not(:disabled)') ?? cover).focus();
        }
        control.inert = under;
        if (under) marked.add(control);
        else marked.delete(control);
      }
    };
    const observer = new ResizeObserver(mark);
    observer.observe(cover);
    observer.observe(controls);
    mark();
    return () => {
      observer.disconnect();
      for (const control of marked) control.inert = false;
    };
  }, [row, bar, covering]);
}
